import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../utils/api';

// Helper function to get week number - moved outside for Calendar component access
function getWeekNumber(date) {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
}

// Helper function to generate calendar for a specific month
function generateCalendar(currentDate) {
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const firstDay = new Date(year, month, 1);
  const startDate = new Date(firstDay);
  
  // Adjust to start from Monday (1) instead of Sunday (0)
  const dayOfWeek = firstDay.getDay();
  const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
  startDate.setDate(startDate.getDate() - daysToSubtract);
  
  const weeks = [];
  const currentWeek = [];
  
  for (let i = 0; i < 42; i++) {
    const date = new Date(startDate);
    date.setDate(startDate.getDate() + i);
    
    currentWeek.push(date);
    
    if (currentWeek.length === 7) {
      weeks.push([...currentWeek]);
      currentWeek.length = 0;
    }
  }
  
  return { weeks, month, year };
}

// Calendar component moved outside to prevent re-creation on each render
const Calendar = ({ currentDate, onDateClick, onClose, selectedYear, selectedWeek }) => {
  const [displayDate, setDisplayDate] = useState(currentDate);
  const { weeks, month, year } = generateCalendar(displayDate);
  
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  
  const isSelectedWeek = (date) => {
    const weekNum = getWeekNumber(date);
    const yearNum = date.getFullYear();
    return weekNum === selectedWeek && yearNum === selectedYear;
  };

  const navigateMonth = (direction) => {
    const newDate = new Date(displayDate);
    newDate.setMonth(displayDate.getMonth() + direction);
    setDisplayDate(newDate);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-4 w-80 shadow-xl">
        <div className="flex items-center justify-between mb-4">
          <button 
            onClick={() => navigateMonth(-1)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h4 className="text-lg font-semibold text-gray-900">
            {monthNames[month]} {year}
          </h4>
          <button 
            onClick={() => navigateMonth(1)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
        
        <div className="grid grid-cols-7 gap-1 mb-2">
          {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
            <div key={day} className="text-center text-sm font-medium text-gray-500 py-2">
              {day}
            </div>
          ))}
        </div>
        
        <div className="grid grid-cols-7 gap-1">
          {weeks.map((week, weekIndex) => (
            week.map((date, dayIndex) => {
              const isCurrentMonth = date.getMonth() === month;
              const isSelected = isSelectedWeek(date);
              
              return (
                <button
                  key={`${weekIndex}-${dayIndex}`}
                  onClick={() => onDateClick(date)}
                  className={`p-2 text-sm rounded-lg transition-colors ${
                    isCurrentMonth ? 'text-gray-900' : 'text-gray-400'
                  } ${
                    isSelected 
                      ? 'bg-blue-500 text-white' 
                      : 'hover:bg-blue-50'
                  }`}
                >
                  {date.getDate()}
                </button>
              );
            })
          ))}
        </div>
        
        <div className="mt-4 text-sm text-gray-600">
          Click on any day to select that week
        </div>
      </div>
    </div>
  );
};

const WorkDiaryFilters = ({ 
  fromYear, 
  setFromYear, 
  fromWeek, 
  setFromWeek, 
  toYear, 
  setToYear, 
  toWeek, 
  setToWeek, 
  selectedSites, 
  setSelectedSites, 
  availableSites 
}) => {
  
  const [sitesData, setSitesData] = useState({});
  const [showFromCalendar, setShowFromCalendar] = useState(false);
  const [showToCalendar, setShowToCalendar] = useState(false);
  const [fromCalendarDate, setFromCalendarDate] = useState(new Date());
  const [toCalendarDate, setToCalendarDate] = useState(new Date());
  
  // Fetch site data for available sites
  useEffect(() => {
    const fetchSitesData = async () => {
      try {
        const response = await axios.get(`${API}/sites`);
        const sitesMap = {};
        response.data.forEach(site => {
          sitesMap[site.id] = site.site_name;
        });
        setSitesData(sitesMap);
      } catch (error) {
        console.error('Failed to fetch sites data:', error);
      }
    };

    if (availableSites.length > 0) {
      fetchSitesData();
    }
  }, [availableSites]);

  // Helper function to get start and end dates of a week (ISO week calculation)
  function getWeekDates(year, weekNum) {
    // Use UTC dates to match getWeekNumber logic exactly
    const jan1 = new Date(Date.UTC(year, 0, 1));
    const jan1Weekday = jan1.getUTCDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
    
    // Convert Sunday (0) to 7 for ISO week calculation
    const jan1Day = jan1Weekday === 0 ? 7 : jan1Weekday;
    
    // Find first Thursday of the year (ISO week 1 contains Jan 4)
    const firstThursday = new Date(Date.UTC(year, 0, 1));
    firstThursday.setUTCDate(1 + (4 - jan1Day + 7) % 7);
    
    // Find the Monday of the first week
    const firstMonday = new Date(firstThursday);
    firstMonday.setUTCDate(firstThursday.getUTCDate() - 3);
    
    // Calculate the start of the requested week
    const weekStart = new Date(firstMonday);
    weekStart.setUTCDate(firstMonday.getUTCDate() + (weekNum - 1) * 7);
    
    const dates = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(weekStart);
      date.setUTCDate(weekStart.getUTCDate() + i);
      dates.push({ date: date.toISOString().split('T')[0], day: date.getUTCDate() });
    }
    return dates;
  }

  // Get week start and end dates for display
  function getWeekStartEnd(year, weekNum) {
    const weekDates = getWeekDates(year, weekNum);
    const startDate = new Date(weekDates[0].date);
    const endDate = new Date(weekDates[6].date);
    
    return {
      start: startDate.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit' }),
      end: endDate.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit' })
    };
  }

  // Format week display
  function formatWeekDisplay(year, weekNum) {
    const weekDates = getWeekStartEnd(year, weekNum);
    return `Week ${weekNum} (${weekDates.start} - ${weekDates.end})`;
  }

  // Handle date click in calendar
  function handleDateClick(date, isFrom = true) {
    const year = date.getFullYear();
    const weekNum = getWeekNumber(date);
    
    if (isFrom) {
      setFromYear(year);
      setFromWeek(weekNum);
      setShowFromCalendar(false);
      
      // If from week is after to week, update to week
      const fromDate = new Date(year, 0, 1);
      fromDate.setDate(fromDate.getDate() + (weekNum - 1) * 7);
      
      const toDate = new Date(toYear, 0, 1);
      toDate.setDate(toDate.getDate() + (toWeek - 1) * 7);
      
      if (fromDate > toDate) {
        setToYear(year);
        setToWeek(weekNum);
      }
    } else {
      setToYear(year);
      setToWeek(weekNum);
      setShowToCalendar(false);
    }
  }

  const handleSiteSelectionChange = (siteId) => {
    setSelectedSites(prev => 
      prev.includes(siteId) 
        ? prev.filter(id => id !== siteId)
        : [...prev, siteId]
    );
  };

  const goToCurrentWeek = () => {
    const today = new Date();
    const currentWeekNum = getWeekNumber(today);
    const currentYearNum = today.getFullYear();
    
    setFromYear(currentYearNum);
    setFromWeek(currentWeekNum);
    setToYear(currentYearNum);
    setToWeek(currentWeekNum);
  };

  return (
    <div className="bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 rounded-xl shadow-lg border border-white/20 p-4 mb-4 backdrop-blur-sm">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg flex items-center justify-center">
            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.707A1 1 0 013 7V4z" />
            </svg>
          </div>
        </div>
        
        {/* Current Week Button - Moved to header */}
        <button
          onClick={goToCurrentWeek}
          className="group relative overflow-hidden bg-white border border-purple-600 hover:bg-purple-50 text-purple-600 hover:text-purple-700 px-4 py-3 sm:py-3 rounded-xl sm:rounded-2xl font-semibold transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 text-sm"
        >
          <span className="relative z-10 flex items-center justify-center">
            <svg className="w-4 h-4 mr-1 group-hover:animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 002 2z" />
            </svg>
            Current Week
          </span>
          <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-10 transition-opacity duration-200"></div>
        </button>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* From Filter */}
        <div className="bg-white/70 backdrop-blur-sm rounded-lg p-3 border border-white/30">
          <h4 className="text-xs font-bold text-blue-900 mb-2 flex items-center">
            <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 002 2z" />
            </svg>
            From Date
          </h4>
          <button
            onClick={() => setShowFromCalendar(true)}
            className="w-full px-2 py-1.5 border border-blue-200 rounded-md text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white/80 backdrop-blur-sm transition-all duration-200 text-left flex items-center justify-between hover:bg-blue-50"
          >
            <span className="truncate">{formatWeekDisplay(fromYear, fromWeek)}</span>
            <svg className="w-3 h-3 text-gray-400 ml-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 002 2z" />
            </svg>
          </button>
        </div>

        {/* To Filter */}
        <div className="bg-white/70 backdrop-blur-sm rounded-lg p-3 border border-white/30">
          <h4 className="text-xs font-bold text-blue-900 mb-2 flex items-center">
            <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 002 2z" />
            </svg>
            To Date
          </h4>
          <button
            onClick={() => setShowToCalendar(true)}
            className="w-full px-2 py-1.5 border border-blue-200 rounded-md text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white/80 backdrop-blur-sm transition-all duration-200 text-left flex items-center justify-between hover:bg-blue-50"
          >
            <span className="truncate">{formatWeekDisplay(toYear, toWeek)}</span>
            <svg className="w-3 h-3 text-gray-400 ml-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 002 2z" />
            </svg>
          </button>
        </div>

        {/* Sites Filter */}
        <div className="bg-white/70 backdrop-blur-sm rounded-lg p-3 border border-white/30">
          <h4 className="text-xs font-bold text-blue-900 mb-2 flex items-center">
            <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
            Site Locations
          </h4>
          <div className="space-y-1 max-h-20 overflow-y-auto">
            {availableSites.length > 0 ? (
              availableSites.map(siteId => (
                <label key={siteId} className="flex items-center space-x-2 text-xs bg-white/50 p-1.5 rounded-md hover:bg-white/70 transition-all duration-200 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedSites.includes(siteId)}
                    onChange={() => handleSiteSelectionChange(siteId)}
                    className="rounded border-blue-300 text-blue-600 focus:ring-blue-500 w-3 h-3"
                  />
                  <span className="text-gray-800 font-medium truncate">
                    {sitesData[siteId] || `Site ${siteId}`}
                  </span>
                </label>
              ))
            ) : (
              <div className="text-center py-2">
                <div className="w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-1">
                  <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <p className="text-xs text-gray-500">No sites assigned</p>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Calendar modals */}
      {showFromCalendar && (
        <Calendar
          currentDate={fromCalendarDate}
          onDateClick={(date) => handleDateClick(date, true)}
          onClose={() => setShowFromCalendar(false)}
          selectedYear={fromYear}
          selectedWeek={fromWeek}
        />
      )}
      
      {showToCalendar && (
        <Calendar
          currentDate={toCalendarDate}
          onDateClick={(date) => handleDateClick(date, false)}
          onClose={() => setShowToCalendar(false)}
          selectedYear={toYear}
          selectedWeek={toWeek}
        />
      )}
    </div>
  );
};

export default WorkDiaryFilters;